import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { BillingSettings, PortalSettings } from 'src/constants/dataTypes';
import { Events } from 'src/constants/webSocketConsts/events';
import { ApiTags } from 'src/services/api';
import { applicationSettingsApi } from 'src/services/api/applicationsApi';
import { automationsApi } from 'src/services/api/automationsApi';
import {
  loadPortalSettingsAction,
  removePortalSettingsAction,
  updateBillingSettingsSucceededAction,
} from 'src/store/settings/actions';
import { ensureUnreachable } from 'src/utils/common_utils';

const portalSettingsHandler = (event: Events, items: Array<PortalSettings>) => {
  if (items.length === 0) {
    return null;
  }
  switch (event) {
    case Events.Remove:
      return handlePortalSettingsChanges(
        items.at(0)?.fields.billingSettings || {},
        items,
        event,
      );
    case Events.Insert:
    case Events.Modify:
      return handlePortalSettingsChanges(
        items.at(0)?.fields.billingSettings || {},
        items,
        event,
      );
    case Events.DomainVerified:
    case Events.Refresh:
      return null;
    default:
      return ensureUnreachable(event);
  }
};

export default portalSettingsHandler;

export const handlePortalSettingsChanges =
  (billingSettings: BillingSettings, items: PortalSettings[], event: Events) =>
  async (dispatch: ThunkDispatch<any, any, AnyAction>) => {
    // Force refetch getInstalls to get fresh data from the server.
    const invalidatedTags = [ApiTags.apps];
    // The per-company client visibility is N parallel /v1/installs calls, so
    // only refetch it when the change actually touches app visibility
    // (module/extension settings) — not on billing-only portal-settings events.
    const affectsAppVisibility = items.some(
      (item) =>
        item.fields.moduleSettings !== undefined ||
        item.fields.extensionsSettings !== undefined,
    );
    if (affectsAppVisibility) {
      invalidatedTags.push(ApiTags.clientCompanyVisibility);
    }
    dispatch(applicationSettingsApi.util.invalidateTags(invalidatedTags));
    if (event === Events.Remove) {
      dispatch(removePortalSettingsAction(items));
    }
    if (event === Events.Insert || event === Events.Modify) {
      dispatch(loadPortalSettingsAction(items));
    }
    dispatch(updateBillingSettingsSucceededAction(billingSettings));
    // invalidate GET templates/actions/triggers to fetch the updated data
    dispatch(automationsApi.util.invalidateTags([ApiTags.automationTemplates]));
    dispatch(automationsApi.util.invalidateTags([ApiTags.automationActions]));
    dispatch(automationsApi.util.invalidateTags([ApiTags.automationTriggers]));
  };
